import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import { getApiErrorMessage } from "./meetingUtils"

export const QUESTION_TYPES = [
  { value: "text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "dropdown", label: "Dropdown" },
  { value: "yes_no", label: "Yes / No" },
]

const SOURCE_NOTES = {
  liaison: "Choices are the user's team.",
  time_zones: "Choices are the booking time zones.",
}

function typeLabel(type) {
  return QUESTION_TYPES.find((option) => option.value === type)?.label || type
}

function QuestionDialog({ open, question, onClose, onSaved }) {
  const isEdit = Boolean(question)
  const [label, setLabel] = useState("")
  const [fieldType, setFieldType] = useState("text")
  const [choices, setChoices] = useState("")
  const [helpText, setHelpText] = useState("")
  const [required, setRequired] = useState(false)
  const [error, setError] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setLabel(question?.label || "")
    setFieldType(question?.field_type || "text")
    setChoices((question?.options || []).join("\n"))
    setHelpText(question?.help_text || "")
    setRequired(Boolean(question?.is_required))
    setError("")
  }, [open, question])

  // A dropdown keeps its own list of choices unless it takes them from the
  // user's team or the time zones.
  const hasOwnChoices = fieldType === "dropdown" && !question?.options_source

  function handleSave(event) {
    event.preventDefault()
    if (!label.trim()) {
      setError("Enter the question.")
      return
    }
    const options = choices
      .split("\n")
      .map((choice) => choice.trim())
      .filter(Boolean)
    if (hasOwnChoices && options.length === 0) {
      setError("Add at least one choice for a dropdown.")
      return
    }
    const payload = {
      label: label.trim(),
      help_text: helpText.trim(),
      is_required: required,
    }
    if (hasOwnChoices) payload.options = options
    if (!isEdit) payload.field_type = fieldType
    setIsSaving(true)
    setError("")
    const request = isEdit
      ? adminApi.updateMeetingQuestion(question.id, payload)
      : adminApi.createMeetingQuestion(payload)
    request
      .then(() => onSaved())
      .catch((requestError) =>
        setError(
          getApiErrorMessage(requestError, "Could not save the question.")
        )
      )
      .finally(() => setIsSaving(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSave}>
        <DialogTitle>{isEdit ? "Edit question" : "Add a question"}</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Question"
              size="small"
              fullWidth
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              inputProps={{ maxLength: 128 }}
            />
            <TextField
              select
              label="Answer type"
              size="small"
              fullWidth
              disabled={isEdit}
              value={fieldType}
              onChange={(event) => setFieldType(event.target.value)}
              helperText={
                isEdit ? "The type can't change once the question exists." : ""
              }
            >
              {QUESTION_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {hasOwnChoices ? (
              <TextField
                label="Choices"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={choices}
                onChange={(event) => setChoices(event.target.value)}
                helperText="One choice per line."
              />
            ) : null}
            {SOURCE_NOTES[question?.options_source] ? (
              <Typography variant="body2" color="text.secondary">
                {SOURCE_NOTES[question.options_source]}
              </Typography>
            ) : null}
            <TextField
              label="Help text (optional)"
              size="small"
              fullWidth
              value={helpText}
              onChange={(event) => setHelpText(event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={required}
                  onChange={(event) => setRequired(event.target.checked)}
                />
              }
              label="Required"
            />
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving}>
            Save question
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

QuestionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  question: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
}

// The questions the booking form asks. The admin booking form and the public
// booking link show this same list.
export default function MeetingQuestionsSection() {
  const [questions, setQuestions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [dialog, setDialog] = useState({ open: false, question: null })
  const [deleting, setDeleting] = useState(null)

  const load = useCallback(() => {
    setIsLoading(true)
    return adminApi
      .fetchMeetingQuestions()
      .then(({ data }) => setQuestions(data.results || []))
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not load the questions.")
        )
      )
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function handleError(error, fallback) {
    setErrorMessage(getApiErrorMessage(error, fallback))
  }

  function handleMove(index, offset) {
    const ids = questions.map((question) => question.id)
    const target = index + offset
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    setErrorMessage("")
    adminApi
      .reorderMeetingQuestions(ids)
      .then(({ data }) => setQuestions(data.results || []))
      .catch((error) => handleError(error, "Could not change the order."))
  }

  function handleToggle(question, field) {
    setErrorMessage("")
    adminApi
      .updateMeetingQuestion(question.id, { [field]: !question[field] })
      .then(load)
      .catch((error) => handleError(error, "Could not update the question."))
  }

  function handleConfirmDelete() {
    const question = deleting
    setDeleting(null)
    setErrorMessage("")
    adminApi
      .deleteMeetingQuestion(question.id)
      .then(load)
      .catch((error) => handleError(error, "Could not delete the question."))
  }

  return (
    <Column gap={2}>
      <Row sx={{ justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 640 }}
        >
          The questions asked when booking a meeting, in this order. The admin
          booking form and the public booking link show the same list. Changes
          apply to new bookings; meetings already booked keep the answers they
          were booked with. Name, phone and email are always asked.
        </Typography>
        <Button
          variant="contained"
          onClick={() => setDialog({ open: true, question: null })}
        >
          Add question
        </Button>
      </Row>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      <Column gap={1}>
        {questions.map((question, index) => (
          <Paper
            key={question.id}
            variant="outlined"
            sx={{ p: 1.5, opacity: question.is_active ? 1 : 0.6 }}
          >
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Column>
                <IconButton
                  size="small"
                  aria-label={`Move ${question.label} up`}
                  disabled={index === 0}
                  onClick={() => handleMove(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Move ${question.label} down`}
                  disabled={index === questions.length - 1}
                  onClick={() => handleMove(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </Column>
              <Column sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="subtitle2">{question.label}</Typography>
                <Row sx={{ gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    {typeLabel(question.field_type)}
                    {question.options.length
                      ? `: ${question.options.join(", ")}`
                      : ""}
                    {SOURCE_NOTES[question.options_source]
                      ? ` · ${SOURCE_NOTES[question.options_source]}`
                      : ""}
                  </Typography>
                  {question.is_builtin ? (
                    <Chip label="Built in" size="small" variant="outlined" />
                  ) : null}
                </Row>
              </Column>
              <FormControlLabel
                label="Required"
                control={
                  <Switch
                    checked={question.is_required}
                    onChange={() => handleToggle(question, "is_required")}
                    inputProps={{ "aria-label": `Require ${question.label}` }}
                  />
                }
              />
              <FormControlLabel
                label="Shown"
                control={
                  <Switch
                    checked={question.is_active}
                    onChange={() => handleToggle(question, "is_active")}
                    inputProps={{ "aria-label": `Show ${question.label}` }}
                  />
                }
              />
              <IconButton
                aria-label={`Edit ${question.label}`}
                onClick={() => setDialog({ open: true, question })}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              {question.is_builtin ? null : (
                <IconButton
                  aria-label={`Delete ${question.label}`}
                  onClick={() => setDeleting(question)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Row>
          </Paper>
        ))}
        {!isLoading && questions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No questions yet. Add one above.
          </Typography>
        ) : null}
      </Column>
      <QuestionDialog
        open={dialog.open}
        question={dialog.question}
        onClose={() => setDialog({ open: false, question: null })}
        onSaved={() => {
          setDialog({ open: false, question: null })
          load()
        }}
      />
      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete this question?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            “{deleting?.label}” will no longer be asked. Meetings already booked
            keep the answers they were booked with.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Keep it</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete question
          </Button>
        </DialogActions>
      </Dialog>
    </Column>
  )
}
